// LabelboxGallery.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Spin, Input, Empty, Pagination, Tooltip, Tag, Space } from "antd";
import { db } from "./FirebaseConfig";
import { LinkOutlined, ScissorOutlined } from "@ant-design/icons";

// ---- タイル（画像＋ポリゴンを1枚表示） ----
function LabelboxTile({ row, width = 300, height = 220 }) {
  const imgRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [draw, setDraw] = useState({ w: width, h: height, offX: 0, offY: 0 });

  const objects = row?.label?.objects || [];
  const onImgLoad = (e) => {
    const img = e.currentTarget;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  };

  useEffect(() => {
    if (!natural) return;
    const ratio = Math.min(width / natural.w, height / natural.h);
    const w = Math.round(natural.w * ratio);
    const h = Math.round(natural.h * ratio);
    const offX = Math.floor((width - w) / 2);
    const offY = Math.floor((height - h) / 2);
    setDraw({ w, h, offX, offY });
  }, [natural, width, height]);

  const palette = ["#ff4d4f", "#40a9ff", "#52c41a", "#faad14", "#722ed1"];

  const resultId = row?.__meta?.resultId ?? "";
  const originalUrl = row?.__meta?.original_image_url;
  const cropUrl = row?.__meta?.cropped_image_url;
  const location = row?.__meta?.location;

  return (
    <div
      style={{
        width,
        height,
        position: "relative",
        background: "#111",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #1f1f1f",
      }}
      title={row?.external_id}
    >
      {/* 画像 */}
      {row?.image_url ? (
        <img
          ref={imgRef}
          src={row.image_url}
          alt={row.external_id}
          onLoad={onImgLoad}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            background: "#000",
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            color: "#999",
            display: "grid",
            placeItems: "center",
          }}
        >
          No image_url
        </div>
      )}

      {/* ポリゴンオーバーレイ */}
      {natural && (
        <svg
          style={{ position: "absolute", inset: 0, width, height, pointerEvents: "none" }}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <g transform={`translate(${draw.offX}, ${draw.offY})`}>
            <g transform={`scale(${draw.w / natural.w}, ${draw.h / natural.h})`}>
              {(objects || []).map((obj, i) => {
                const color = palette[i % palette.length];
                const pts = obj?.polygon?.path || [];
                if (!pts.length) return null;
                const d =
                  pts.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") + " Z";
                return (
                  <g key={i}>
                    <path d={d} fill={`${color}33`} stroke={color} strokeWidth={2} />
                    {pts.map((p, k) => (
                      <circle key={k} cx={p.x} cy={p.y} r={2.5} fill="#fff" stroke={color} strokeWidth={1.2} />
                    ))}
                  </g>
                );
              })}
            </g>
          </g>
        </svg>
      )}

      {/* 右上：ID / 位置情報バッジ */}
      <div style={{ position: "absolute", top: 6, right: 6 }}>
        {resultId && <Tag color="gold">ID: {resultId}</Tag>}
        {location && (
          <Tooltip
            title={`left:${location.left}, top:${location.top}, right:${location.right}, bottom:${location.bottom}`}
          >
            <Tag color="blue">loc</Tag>
          </Tooltip>
        )}
      </div>

      {/* フッター（ファイル名など） */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.65))",
          color: "#eee",
          fontSize: 12,
          padding: "18px 8px 6px",
          textShadow: "0 1px 2px rgba(0,0,0,0.8)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 8,
        }}
      >
        <div style={{ maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row?.external_id}
        </div>
        <Space size={6}>
          {originalUrl && (
            <a href={originalUrl} target="_blank" rel="noreferrer" title="Open original">
              <Tooltip title="Open original">
                <LinkOutlined style={{ color: "#91d5ff" }} />
              </Tooltip>
            </a>
          )}
          {cropUrl && (
            <a href={cropUrl} target="_blank" rel="noreferrer" title="Open crop">
              <Tooltip title="Open crop">
                <ScissorOutlined style={{ color: "#ffd666" }} />
              </Tooltip>
            </a>
          )}
        </Space>
      </div>
    </div>
  );
}

// ---- ギャラリー全体 ----
export default function LabelboxGallery({
  width = 300,
  height = 220,
  gap = 12,
  columns = 4,
  pageSize = 24,
}) {
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]); // 展開後 row 配列（__meta付き）
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "labelbox_data"));
        const rows = [];
        snap.forEach((docSnap) => {
          const d = docSnap.data();
          const arr = Array.isArray(d?.data) ? d.data : [];
          const meta = d?.meta || null;
          // 1ドキュメント内の複数行を展開して meta を同梱
          arr.forEach((row) => {
            rows.push({
              ...row,
              __meta: meta, // ← ここに original_image_url / cropped_image_url / resultId / location / photopath / cultiverName 等が入る
            });
          });
        });
        setDocs(rows);
      } catch (e) {
        console.error("読み込みエラー:", e);
        setDocs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 検索：external_id / resultId / photopath / cultiverName を対象
  const filtered = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    if (!keyword) return docs;
    return docs.filter((r) => {
      const ex = (r?.external_id || "").toLowerCase();
      const id = (r?.__meta?.resultId || "").toLowerCase();
      const path = (r?.__meta?.photopath || "").toLowerCase();
      const cultivar = (r?.__meta?.cultiverName || r?.__meta?.cultivarName || "").toLowerCase();
      return ex.includes(keyword) || id.includes(keyword) || path.includes(keyword) || cultivar.includes(keyword);
    });
  }, [docs, q]);

  // ページング
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageRows = filtered.slice(start, end);

  return (
    <div>
      {/* ヘッダ */}
      <div style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <Input.Search
          placeholder="external_id / resultId / cultivar で検索"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={setQ}
          style={{ width: 360 }}
        />
        <div style={{ color: "#999" }}>
          {filtered.length} items {loading ? "(loading…)" : ""}
        </div>
      </div>

      {/* グリッド */}
      {loading ? (
        <div style={{ display: "grid", placeItems: "center", height: 240 }}>
          <Spin />
        </div>
      ) : pageRows.length === 0 ? (
        <div style={{ display: "grid", placeItems: "center", height: 240 }}>
          <Empty description="データがありません" />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, ${width}px)`,
            gap,
          }}
        >
          {pageRows.map((row, i) => (
            <LabelboxTile key={`${row?.external_id}-${i}`} row={row} width={width} height={height} />
          ))}
        </div>
      )}

      {/* ページネーション */}
      {filtered.length > pageSize && (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={filtered.length}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
}
